import { ObjectWithStringSignature } from "./componentTypes";
import React, {
  ForwardedRef,
  KeyboardEventHandler,
  ReactElement,
  useEffect,
} from "react";
import { useImmer } from "use-immer";
import { Input } from "./ui/Input";
import { Label } from "./ui/Label";
import { cn } from "../lib/utils";

export interface Plan2PlateInputProps<
  T extends ObjectWithStringSignature,
  K extends keyof T & string,
  V extends number | string,
> {
  fieldName: K;
  leftAddon?: boolean;
  leftAddonLabel?: string;
  leftAddonMinW?: string;
  rightAddon?: boolean;
  rightAddonLabel?: string;
  rightAddonMinW?: string;
  placeholder?: string;
  dataCy?: string;
  value: T[K];
  updater: (fieldname: K, value: V) => T;
  handleBlur?: (newValue: T) => void;
  inputType: V extends string ? "string" : V extends number ? "number" : never;
  valueConverter: (inputValue: string) => V;
  width?: string;
  padding?: string;
  disabled?: boolean;
  valueFormatter?: (value: T[K]) => string;
  isInvalid?: boolean;
  onKeyDown?: KeyboardEventHandler<any>;
}

const Plan2PlateInputInternal = <
  T extends ObjectWithStringSignature,
  K extends keyof T & string,
  V extends number | string,
>(
  props: Plan2PlateInputProps<T, K, V>,
  ref: ForwardedRef<HTMLInputElement>,
): ReactElement => {
  const {
    fieldName,
    leftAddonLabel,
    rightAddonLabel,
    placeholder,
    dataCy,
    value,
    updater,
    handleBlur,
    inputType,
    valueConverter,
    width,
    padding,
    disabled,
    isInvalid,
    onKeyDown,
  } = props;
  const valueFormatter = props.valueFormatter || ((v: T[K]) => `${v}`);
  const [currentValue, updateCurrentValue] = useImmer(valueFormatter(value));
  useEffect(() => {
    updateCurrentValue(valueFormatter(value));
  }, [value]);
  const caption =
    leftAddonLabel != null
      ? rightAddonLabel != null
        ? `${leftAddonLabel} (${rightAddonLabel})`
        : leftAddonLabel
      : null;
  return (
    <div
      className="flex flex-col items-stretch"
      style={{ width, padding }}
    >
      {caption != null && <Label>{caption}</Label>}
      <Input
        ref={ref}
        data-cy={dataCy}
        disabled={disabled}
        type={inputType}
        name={fieldName}
        placeholder={placeholder}
        value={currentValue || ""}
        aria-invalid={isInvalid || undefined}
        className={cn(isInvalid && "border-protein")}
        onChange={(e) => updateCurrentValue(() => e.target.value)}
        onBlur={() => {
          const updatedValue = valueConverter(currentValue);
          const updatedObj = updater(fieldName, updatedValue);
          updateCurrentValue(valueFormatter(updatedValue as T[K]));
          handleBlur && handleBlur(updatedObj);
        }}
        onFocus={(e) => e.target.select()}
        onKeyDown={onKeyDown}
      />
    </div>
  );
};

export const Plan2PlateInput = React.forwardRef(Plan2PlateInputInternal) as <
  T extends ObjectWithStringSignature,
  K extends keyof T & string,
  V extends number | string,
>(
  props: Plan2PlateInputProps<T, K, V> & { ref?: React.Ref<HTMLInputElement> },
) => ReactElement;
